import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.scss'
import reportWebVitals from './reportWebVitals'
import { RouterProvider } from 'react-router-dom'
import { routes } from './routes'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ToastContainer, Zoom } from 'react-toastify'
import { AuthProvider } from 'context/authContext'
import 'styles/globals.scss'
import { Provider } from 'react-redux'
import { persistor, store } from 'store/redux'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js'
import { PersistGate } from 'redux-persist/integration/react'
import {
    bootstrapMiniapp,
    installMiniappBackButton,
    isMiniapp,
} from 'modules/miniapp'
import { ensureSessionProfile } from 'utilities/session'
import { trackHistoryPaths } from 'utilities/navigation/flow'
import { restoreLandingAttribution } from 'modules/landing/code'
import { installPersianDigitInput } from 'utilities/persianDigits'
import TelemetryFlusher from 'components/miniapp/telemetryFlusher'
import WhatsNew, { APP_VERSION } from 'modules/whatsNew'
import { queueRegister } from 'utilities/analytics'
import { applyAppUpdate, seedCurrentBuild } from 'modules/appUpdate'
import {
    installWebPushFirstOpen,
    installWebPushOpenHandling,
    syncWebPush,
} from 'modules/webPush'
import { readGatewayReturn, takeGatewayHandoff } from 'modules/gatewayHandoff'
import GatewayHandoffSheet from 'modules/gatewayHandoff/sheet'
import GatewayReturnScreen from 'modules/gatewayHandoff/returnScreen'
// Ingestion goes through PostHog's managed reverse proxy on our own domain
// instead of *.i.posthog.com, which ad/tracker blockers drop by hostname —
// that silently costs us events and session recordings from exactly the users
// most likely to hit bugs. Hardcoded rather than read from
// REACT_APP_PUBLIC_POSTHOG_HOST: an unset env var inlines `undefined` at build
// time with no error and posthog-js quietly falls back to the blocked host.
const POSTHOG_PROXY_HOST = 'https://record.gethamyan.ir'

const options = {
    api_host: POSTHOG_PROXY_HOST,
    // Only ingestion is proxied. ui_host tells the SDK where the PostHog app
    // itself lives so links it builds (get_session_replay_url, the toolbar)
    // point at PostHog and not back at our proxy, which serves no UI.
    ui_host: 'https://us.posthog.com',
    // Anonymous browsing stays cheap; a person profile is created on identify()
    // (after login), which back-links the pre-login events into that person.
    person_profiles: 'identified_only' as const,
    // Capture the initial pageview automatically. SPA route changes are captured
    // manually below (posthog-js < 1.160 does not auto-capture history changes).
    capture_pageview: true,
    capture_pageleave: true,
    // We do not run surveys, and leaving them on is not free here. PostHog
    // fetches `/api/surveys/` roughly once a second and loads `surveys.js` from
    // us-assets.i.posthog.com — a host that is blocked from Iran, so every one
    // of those requests fails with status 0 and the cross-origin script throws
    // an untraceable "Script error.".
    //
    // Measured on one 269-second mini-app session: 173 failed survey requests
    // and 69 "Script error." console errors, none of them ours. That is what
    // makes healthy sessions show three-digit `console_error_count` and drowns
    // real errors in the replay UI.
    disable_surveys: true,
}

// posthog-js 1.147 only fires $pageview on the initial hard load, so client-side
// navigations (React Router) go untracked. Emit a $pageview whenever the path
// changes so page-based funnels and path analytics see the full journey.
// what lets a finished flow take its own screens out of history
trackHistoryPaths(routes)

let lastTrackedPath = window.location.pathname
routes.subscribe((state) => {
    const path = state.location.pathname
    if (path !== lastTrackedPath) {
        lastTrackedPath = path
        posthog.capture('$pageview')
    }
})
const queryClient = new QueryClient({
    defaultOptions: {
        mutations: {
            retry: false,
        },
        queries: {
            refetchOnWindowFocus: true,
            retry: false,
        },
    },
})

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

const render = () =>
    root.render(
        <React.StrictMode>
            <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                    <PostHogProvider
                        apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
                        options={options}
                    >
                        <QueryClientProvider client={queryClient}>
                            <AuthProvider>
                                <TelemetryFlusher />
                                <RouterProvider router={routes} />
                                <WhatsNew router={routes} />
                                <GatewayHandoffSheet router={routes} />
                                <ToastContainer
                                    position="top-center"
                                    className="text-right"
                                    transition={Zoom}
                                    hideProgressBar={true}
                                />
                            </AuthProvider>
                        </QueryClientProvider>
                    </PostHogProvider>
                </PersistGate>
            </Provider>
        </React.StrictMode>
    )

// Persian digits are what a Persian keyboard produces, so treat «۱۲۳» as 123
// in every field in the app. Installed before the first render so the very
// first keystroke on the login screen is already covered.
installPersianDigitInput()

// Inside Bale, settle the session *before* the first render. AuthProvider seeds
// itself from localStorage on mount and the router has no ProtectedRoute — auth
// is enforced by the first API call 401ing — so rendering first would flash a
// login screen at an already-authenticated user and throw away the launch
// payload in the redirect. bootstrapMiniapp never throws and never blocks on
// the Bale CDN; on the open web isMiniapp() is false and this costs nothing.
const start = async () => {
    // Every event says which build sent it (modules/whatsNew/releases.ts), so a
    // spike in errors or a dip in a funnel can be pinned to a deploy.
    queueRegister({ app_version: APP_VERSION })

    // Re-attach the campaign invitation this browser arrived on, if any, so a
    // login or a fund created days later still carries it. Cheap, silent, and
    // a no-op for everyone who never came in on a landing link.
    restoreLandingAttribution()

    // Every entry point owes the app a cached profile — a dozen components read
    // it while rendering and the sidebar bounces you without one. The SMS login
    // gets there via /auth/profile; anything else (admin impersonate today,
    // Telegram/Eitaa next) would otherwise land authenticated but unusable.
    // With no session, or one already cached, this is a no-op — so a normal web
    // visit pays nothing.
    const redirect = isMiniapp()
        ? await bootstrapMiniapp()
        : await ensureSessionProfile()

    render()

    // Navigated through the router, not history.replaceState: `routes` captures
    // its location when routes.tsx is imported, which already happened above,
    // so rewriting history here would be silently ignored.
    if (redirect) routes.navigate(redirect, { replace: true })

    // Claim the Android back key from the messenger. After the redirect above,
    // so the front door is not counted as a screen to go back from. No-op
    // outside a mini app.
    installMiniappBackButton(routes)

    // Web push: count and route a tap on one of our notifications, then make
    // sure a browser that opted in is still subscribed. Both are silent.
    installWebPushOpenHandling(routes, !!redirect)
    // Last, so that the ask below sees what the sync left behind: the browser's
    // own permission dialog, once per browser, on the first open of the app.
    // It waits for a session and for a tap of theirs (modules/webPush).
    void syncWebPush().finally(installWebPushFirstOpen)
}

const startApp = () => {
    start().catch(render)
    startServiceWorker()
}

// A mini app inside a messenger's WEB client (an iframe) cannot show a bank
// gateway, so it opens one in a new tab of the browser — see
// modules/gatewayHandoff. That tab is this same bundle in one of two roles,
// and neither is the app:
//
//   - the hop on its way out (`/#gw_handoff=…`): mark the tab and go to the
//     bank. Nothing renders, no pageview carries the payment link anywhere and
//     no service worker installs in a tab that will not stay;
//   - the bank bringing it back (`/funds/?result=…`): show the verdict and the
//     way back to the messenger. The app itself could not run here — this tab
//     does not share the frame's storage, so it has no session.
const handingOff = takeGatewayHandoff()
const gatewayReturn = handingOff ? null : readGatewayReturn()
if (gatewayReturn) {
    root.render(
        <React.StrictMode>
            <PostHogProvider
                apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
                options={options}
            >
                <GatewayReturnScreen {...gatewayReturn} />
            </PostHogProvider>
        </React.StrictMode>
    )
} else if (!handingOff) {
    startApp()
}

// Activate a newly deployed version instead of sitting on the old one.
//
// register() used to be called with no config, so onUpdate did nothing: the new
// service worker installed and then stayed in `waiting` forever. Reloading did
// NOT help -- a waiting worker only takes over once every tab of the origin is
// closed -- so a deploy was invisible to anyone with the app already open.
// service-worker.ts already listens for SKIP_WAITING; nothing ever sent it.
//
// onUpdate is NOT the same thing as a deploy, though: iOS Safari re-installs
// the identical script again and again, and reloading on each of those is what
// made the app refresh itself in a loop. modules/appUpdate decides whether the
// waiting worker is really a new build before it reloads anyone -- for which it
// needs to know which build this page is already on.
function startServiceWorker() {
    void seedCurrentBuild()
    serviceWorkerRegistration.register({
        onUpdate: (registration) => {
            void applyAppUpdate(registration)
        },
    })
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
//fucking test
//fucking test 2
